import { addDays, format, isValid, parseISO } from 'date-fns';

/** What the former `moment(value)` calls received: a Date, an ISO string, a timestamp, nothing, or a Moment-like object. */
export type DateInput = Date | string | number | null | undefined | { toDate(): Date };

/** Start of an ISO 8601 date (2026-10-16, +002026-10-16, 2026-W42…), as moment recognises it. */
const ISO_DATE = /^\s*(?:[+-]\d{6}|\d{4})-(?:\d\d|W\d\d)/;
/** Year, month and day of an ISO calendar date, which date-fns rolls over (2026-02-29 becomes 1 March). */
const ISO_CALENDAR_DATE = /^\s*([+-]\d{6}|\d{4})-(\d\d)-(\d\d)(?!\d)/;

/**
 * Converts a value to a Date the way `moment(value)` did:
 * `undefined` is now, `null` is an invalid date, strings are read as ISO 8601 (local time when
 * there is no offset) and otherwise handed to `new Date()`, like moment's fallback.
 */
export function toDate(value?: DateInput): Date {
    if (value === undefined) {
        return new Date();
    }
    if (value === null) {
        return new Date(NaN);
    }
    if (value instanceof Date) {
        return new Date(value.getTime());
    }
    if (typeof value === 'number') {
        return new Date(value);
    }
    if (typeof value === 'string') {
        // moment only falls back to new Date() for strings that are not ISO 8601 at all:
        // an ISO date that does not exist (2026-02-29) stays invalid.
        if (!ISO_DATE.test(value)) {
            return new Date(value);
        }
        const calendar = ISO_CALENDAR_DATE.exec(value);
        if (calendar) {
            const [, year, month, day] = calendar.map(Number);
            const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
            if (month < 1 || month > 12 || day < 1 || day > daysInMonth) {
                return new Date(NaN);
            }
        }
        return parseISO(value.trim());
    }
    if (typeof value.toDate === 'function') {
        return new Date(value.toDate().getTime());
    }
    return new Date(NaN);
}

/** `moment(value).isValid()` */
export function isValidDate(value?: DateInput): boolean {
    return isValid(toDate(value));
}

/**
 * `moment(value).format(...)`, with date-fns tokens (`dd/MM/yyyy`, not `DD/MM/YYYY`).
 * An invalid date gives "Invalid date", like moment, instead of throwing.
 */
export function formatDate(value: DateInput, pattern: string): string {
    const date = toDate(value);
    return isValid(date) ? format(date, pattern) : 'Invalid date';
}

/** Rounds toward zero, like moment's `absFloor`. */
function absFloor(value: number): number {
    return value < 0 ? Math.ceil(value) || 0 : Math.floor(value);
}

/**
 * `moment(a).diff(moment(b), 'days')`: whole days between the two dates, counted toward zero,
 * with moment's correction for a daylight saving change between them. NaN if either is invalid.
 */
export function diffInDays(a: DateInput, b: DateInput): number {
    const from = toDate(a);
    const to = toDate(b);
    const zoneDelta = (from.getTimezoneOffset() - to.getTimezoneOffset()) * 60_000;
    return absFloor((from.getTime() - to.getTime() - zoneDelta) / 86_400_000);
}

/** `moment(a).diff(moment(b), 'minutes')` */
export function diffInMinutes(a: DateInput, b: DateInput): number {
    return absFloor((toDate(a).getTime() - toDate(b).getTime()) / 60_000);
}

/** `a >= b`, false when either date is invalid (`moment(a).isSameOrAfter(b)`). */
export function isSameOrAfter(a: DateInput, b: DateInput): boolean {
    return toDate(a).getTime() >= toDate(b).getTime();
}

/** `a > b`, false when either date is invalid (`moment(a).isAfter(b)`). */
export function isAfter(a: DateInput, b: DateInput): boolean {
    return toDate(a).getTime() > toDate(b).getTime();
}

/** `a < b`, false when either date is invalid (`moment(a).isBefore(b)`). */
export function isBefore(a: DateInput, b: DateInput): boolean {
    return toDate(a).getTime() < toDate(b).getTime();
}

/** Adds working days (Monday to Friday) to a date. */
export function addBusinessDays(date: DateInput, days: number): Date {
    let result = toDate(date);
    let remaining = days;
    while (remaining > 0) {
        result = addDays(result, 1);
        if (result.getDay() !== 0 && result.getDay() !== 6) {
            remaining--;
        }
    }
    return result;
}

/** Days, hours, minutes and seconds of a duration, as `moment.duration(value, unit)` gives them. */
export function durationParts(value: number, unit: 'minutes' | 'milliseconds'): { asDays: number; hours: number; minutes: number; seconds: number } {
    const ms = unit === 'minutes' ? value * 60_000 : value;
    const seconds = absFloor(ms / 1000);
    const minutes = absFloor(seconds / 60);
    const hours = absFloor(minutes / 60);
    return {
        asDays: ms / 86_400_000,
        hours: hours % 24,
        minutes: minutes % 60,
        seconds: seconds % 60,
    };
}
